import { useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { useFormEngineForm, useFormEngineState } from '../context/FormEngineContext';
import { evaluateFieldState } from '../processors/fieldStateEvaluator';
import { computeReactionOverrides } from '../processors/reactionProcessor';
import { prefixFieldConditions } from '../utils/prefixFieldConditions';
import { extractMatrixVisibilityWatchPaths } from '../utils/extractFieldWatchPaths';
import { evaluateCondition, type ConditionContext } from '../evaluators/conditionEvaluator';
import type { Condition } from '../types/conditions';
import type { FormFieldMapping } from '../types/fieldMapping';
import type { FormSpacerEntry, FormSectionDividerEntry, FormInlineEachEntry, FormFieldStackEntry } from '../types/layout';

interface InlineActionButtonForVisibility {
  type: 'inline-action-button';
  actionId: string;
  hiddenWhen?: Condition;
}

type FieldEntry = FormFieldMapping | FormSpacerEntry | FormSectionDividerEntry | FormInlineEachEntry | FormFieldStackEntry | InlineActionButtonForVisibility;

interface UseMatrixVisibilityProps {
  fields: FieldEntry[];
  namePrefix?: string;
}

function isInlineActionButton(entry: FieldEntry): entry is InlineActionButtonForVisibility {
  return 'type' in entry && entry.type === 'inline-action-button';
}

function isNonFieldEntry(entry: FieldEntry): entry is FormSpacerEntry | FormSectionDividerEntry | FormInlineEachEntry | FormFieldStackEntry {
  return 'type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider' || entry.type === 'inline-each' || entry.type === 'stack');
}

function fieldEntryId(entry: FieldEntry, idx: number): string {
  if ('type' in entry && entry.type === 'inline-each') return `inline-each-${entry.node.name}-${idx}`;
  if ('type' in entry && entry.type === 'stack') return `stack-${idx}`;
  if (!('type' in entry)) return (entry as FormFieldMapping).name;
  if (entry.type === 'section-divider') return `section-divider-${idx}`;
  if (entry.type === 'spacer') return `spacer-${idx}`;
  if (entry.type === 'inline-action-button') return `inline-action-btn-${(entry as InlineActionButtonForVisibility).actionId}-${idx}`;
  return (entry as unknown as FormFieldMapping).name;
}

/**
 * Scoped visibility map for matrix layout — watches only condition/reaction dependency paths
 * instead of the entire form value tree.
 */
export function useMatrixVisibility({ fields, namePrefix }: UseMatrixVisibilityProps): Record<string, boolean> {
  const { form, hydratingRef, hydrationEpochRef } = useFormEngineForm();
  const { meta, isReadOnly, getOptionProp, modeOverrides } = useFormEngineState();

  const targetedPaths = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => extractMatrixVisibilityWatchPaths(fields as any, namePrefix),
    [fields, namePrefix],
  );

  const watchedValues = useWatch({
    control: form.control,
    name: targetedPaths,
    disabled: targetedPaths.length === 0,
  });

  const visibilityMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    const formValues = form.getValues() as Record<string, unknown>;

    fields.forEach((entry, idx) => {
      const fieldKey = fieldEntryId(entry, idx);

      if (isInlineActionButton(entry)) {
        const btn = entry as InlineActionButtonForVisibility;
        if (btn.hiddenWhen) {
          const condCtx: ConditionContext = {
            fieldValue: undefined,
            values: formValues,
            meta,
            getOptionProp,
          };
          const isHidden = evaluateCondition(btn.hiddenWhen, condCtx);
          map[fieldKey] = !isHidden;
        } else {
          map[fieldKey] = true;
        }
        return;
      }

      if (isNonFieldEntry(entry)) {
        map[fieldKey] = true;
        return;
      }

      const field = entry as FormFieldMapping;
      const prefixedField = prefixFieldConditions(field, namePrefix);
      const layoutOverride = modeOverrides?.fieldLayout?.[field.name];

      if (layoutOverride?.hidden) {
        map[fieldKey] = false;
        return;
      }

      const reactionOverrides = computeReactionOverrides({
        reactions: prefixedField.reactions,
        formValues,
        meta,
        getOptionProp,
        namePrefix,
      });

      const fieldState = evaluateFieldState(prefixedField, {
        formValues,
        meta,
        getOptionProp,
        isGlobalReadOnly: isReadOnly,
        modeOverrides,
        reactionOverrides,
        namePrefix,
      });

      map[fieldKey] = fieldState.visible;
    });

    return map;
  }, [fields, watchedValues, form, meta, isReadOnly, modeOverrides, namePrefix, getOptionProp]);

  const prevVisibilityRef = useRef<Record<string, boolean>>({});
  const prevEpochRef = useRef(hydrationEpochRef?.current ?? 0);

  useEffect(() => {
    // During hydration, form.reset() triggers useWatch → visibilityMap recalc →
    // this effect. Visibility may transiently flip as field values change from
    // defaults to API data. Nulling values here would destroy hydrated data.
    // Still sync prevVisibilityRef so post-hydration transitions compare correctly.
    if (hydratingRef.current) {
      fields.forEach((entry, idx) => {
        if (isNonFieldEntry(entry)) return;
        if (isInlineActionButton(entry)) return;
        const fieldKey = fieldEntryId(entry, idx);
        prevVisibilityRef.current[fieldKey] = visibilityMap[fieldKey] ?? true;
      });
      return;
    }

    if (hydrationEpochRef && hydrationEpochRef.current !== prevEpochRef.current) {
      prevEpochRef.current = hydrationEpochRef.current;
      fields.forEach((entry, idx) => {
        if (isNonFieldEntry(entry)) return;
        if (isInlineActionButton(entry)) return;
        const fieldKey = fieldEntryId(entry, idx);
        prevVisibilityRef.current[fieldKey] = visibilityMap[fieldKey] ?? true;
      });
      return;
    }

    const formValues = form.getValues() as Record<string, unknown>;

    fields.forEach((entry, idx) => {
      if (isNonFieldEntry(entry)) return;
      if (isInlineActionButton(entry)) return;

      const field = entry as FormFieldMapping;
      const fieldKey = fieldEntryId(entry, idx);
      const fieldName = namePrefix ? `${namePrefix}.${field.name}` : field.name;
      const isVisible = visibilityMap[fieldKey] ?? true;
      const wasVisible = prevVisibilityRef.current[fieldKey];

      if (wasVisible === undefined) {
        prevVisibilityRef.current[fieldKey] = isVisible;
        return;
      }

      if (wasVisible && !isVisible) {
        const prefixedField = prefixFieldConditions(field, namePrefix);
        const reactionOverrides = computeReactionOverrides({
          reactions: prefixedField.reactions,
          formValues,
          meta,
          getOptionProp,
          namePrefix,
        });
        const fieldState = evaluateFieldState(prefixedField, {
          formValues,
          meta,
          getOptionProp,
          isGlobalReadOnly: isReadOnly,
          modeOverrides,
          reactionOverrides,
          namePrefix,
        });

        if (fieldState.clearOnHide !== false) {
          form.setValue(fieldName as never, null as never, {
            shouldDirty: false,
            shouldValidate: false,
          });
        }
      }

      prevVisibilityRef.current[fieldKey] = isVisible;
    });
  }, [
    visibilityMap,
    fields,
    form,
    namePrefix,
    meta,
    getOptionProp,
    isReadOnly,
    modeOverrides,
    hydratingRef,
    hydrationEpochRef,
  ]);

  return visibilityMap;
}
